import axios from "axios";
import { redirect, type LoaderFunctionArgs } from "react-router-dom";

import { checkTokenAuthStatus } from "@pages/LoginManagement/api.ts";
import { resolveInviteSlug } from "@/utils/api/resolveInviteSlug";
import { shouldRedirectLegacyRegisterToIntake } from "@/utils/frontendMigration";
import { buildIntakeCreateAccountUrl } from "@/utils/intakeUrl";

/**
 * Redirect legacy /:businessSlug/register links to intake signup when the
 * business has fully migrated to Smart Intake.
 */
export async function legacyRegisterLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const url = new URL(request.url);

  if (url.searchParams.has("token")) {
    return null;
  }

  try {
    await checkTokenAuthStatus();
    return null;
  } catch {
    // Unauthenticated visitors may be redirected below.
  }

  if (!params.businessSlug) {
    throw new Response("Not Found", { status: 404 });
  }

  try {
    const resolved = await resolveInviteSlug(params.businessSlug);

    if (
      !shouldRedirectLegacyRegisterToIntake(
        resolved.frontendMode,
        resolved.intakeFrontendMigrationEnabled
      )
    ) {
      return null;
    }

    const intakeUrl = buildIntakeCreateAccountUrl({
      businessId: resolved.businessId,
      businessName: resolved.businessName ?? "",
      assigneeId: url.searchParams.get("assigneeId") ?? undefined,
      email: url.searchParams.get("email") ?? undefined,
      firstName:
        url.searchParams.get("firstName") ??
        url.searchParams.get("first_name") ??
        undefined,
      lastName:
        url.searchParams.get("lastName") ??
        url.searchParams.get("last_name") ??
        undefined,
      phoneNumber:
        url.searchParams.get("phoneNumber") ??
        url.searchParams.get("phone") ??
        undefined,
    });

    return redirect(intakeUrl);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Response("Not Found", { status: 404 });
    }
    throw error;
  }
}
